import {
  LANGUAGE_STORAGE_KEY,
  LOCALE_SUGGEST_CACHE_KEY,
  Language,
  type LanguageValue,
  parseBrowserLanguage,
  resolveLanguageFromLocation,
} from '@paladin-monorepo/common';

import API from '@/lib/api';

type LocaleSuggestResponse = {
  country: string | null;
};

export function getStoredLanguage(): LanguageValue | null {
  try {
    const stored = localStorage.getItem(LANGUAGE_STORAGE_KEY);
    return stored === Language.CS || stored === Language.EN ? stored : null;
  } catch {
    return null;
  }
}

export function setStoredLanguage(language: LanguageValue): void {
  localStorage.setItem(LANGUAGE_STORAGE_KEY, language);
}

export function applyDocumentLanguage(language: LanguageValue): void {
  document.documentElement.lang = language;
}

function getCachedLocaleSuggest(): LocaleSuggestResponse | null {
  try {
    const raw = sessionStorage.getItem(LOCALE_SUGGEST_CACHE_KEY);
    return raw ? (JSON.parse(raw) as LocaleSuggestResponse) : null;
  } catch {
    return null;
  }
}

function setCachedLocaleSuggest(value: LocaleSuggestResponse): void {
  sessionStorage.setItem(LOCALE_SUGGEST_CACHE_KEY, JSON.stringify(value));
}

async function fetchLocaleSuggest(): Promise<LocaleSuggestResponse | null> {
  const cached = getCachedLocaleSuggest();
  if (cached) {
    return cached;
  }

  try {
    const response = await API.get<LocaleSuggestResponse>('/locale/suggest');
    setCachedLocaleSuggest(response.data);
    return response.data;
  } catch {
    return null;
  }
}

export async function resolveAppLanguage(): Promise<LanguageValue> {
  const stored = getStoredLanguage();
  if (stored) {
    return stored;
  }

  const browserLanguage =
    typeof navigator !== 'undefined'
      ? parseBrowserLanguage(navigator.languages, navigator.language)
      : null;

  if (browserLanguage) {
    return browserLanguage;
  }

  const localeSuggest = await fetchLocaleSuggest();
  const fromLocation = resolveLanguageFromLocation({
    country: localeSuggest?.country,
    timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
  });

  return fromLocation ?? Language.CS;
}
